import { Clock } from 'lucide-react'
import { STATUS_META, renderReasonLabel, decodeHtmlEntities } from './liveControlsUtils'

export default function MeetingPulseBar({
  topicRows = [],
  activeAgendaItemId,
  activeAgendaItemTitle,
  topicSummary,
  isLive,
}) {
  const hasTopics = topicRows.length > 0
  // Show which agenda item we're on (1-indexed).
  // Find the active item's position; fall back to counting completed items.
  const total = topicRows.length
  const activeIdx = topicRows.findIndex((r) => r.agendaItemId === activeAgendaItemId)
  const coveredCount = (topicSummary?.covered ?? 0) + (topicSummary?.coveredWeakConfidence ?? 0)
  const currentItemNumber =
    activeIdx >= 0
      ? activeIdx + 1
      : coveredCount

  // No-agenda fallback
  if (!hasTopics) {
    return (
      <div className="sticky top-0 z-20 flex h-[52px] items-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 px-4 text-xs text-gray-400 shadow-sm backdrop-blur-sm">
        <Clock className="h-3.5 w-3.5" />
        No agenda items
      </div>
    )
  }

  return (
    <div className="sticky top-0 z-20 flex flex-col rounded-2xl bg-white/95 px-4 backdrop-blur-sm shadow-sm">
      {/* Row 1: Progress bar + counter + timer */}
      <div className="flex h-[44px] items-center gap-3">
        {/* Segmented progress bar */}
        <div
          role="progressbar"
          aria-label="Agenda progress"
          aria-valuenow={currentItemNumber}
          aria-valuemax={topicRows.length}
          className="hidden sm:flex h-[18px] min-w-0 flex-1 items-center gap-0.5 rounded-full overflow-hidden"
        >
          {topicRows.map((row) => {
            const isActive = row.agendaItemId === activeAgendaItemId
            const bgClass =
              row.lifecycleStatus === 'covered'
                ? 'bg-emerald-600'
                : row.lifecycleStatus === 'covered_weak_confidence'
                  ? 'bg-emerald-200'
                : row.lifecycleStatus === 'at_risk'
                  ? 'bg-red-400'
                  : row.lifecycleStatus === 'in_progress'
                    ? 'bg-emerald-400'
                    : 'bg-gray-200'

            return (
              <div
                key={row.agendaItemId}
                title={`${decodeHtmlEntities(row.title)} — ${(STATUS_META[row.lifecycleStatus] ?? STATUS_META.not_started).label}${
                  row.lifecycleStatus === 'at_risk' && row.statusReasonCodes?.length
                    ? ` (${row.statusReasonCodes.map(renderReasonLabel).join(', ')})`
                    : ''
                }`}
                style={{ flex: 1 }}
                className={`h-full transition-all duration-500 ${bgClass} ${
                  isActive ? 'ring-2 ring-offset-1 ring-emerald-500 rounded-sm' : ''
                }`}
              />
            )
          })}
        </div>

        {/* Coverage fraction counter */}
        <span className="hidden sm:inline shrink-0 text-xs font-semibold text-slate-500 tabular-nums">
          {currentItemNumber}/{topicRows.length}
        </span>

        {/* Mobile summary (sm:hidden) */}
        <div className="sm:hidden min-w-0 flex-1 text-xs text-slate-600 truncate">
          {activeAgendaItemTitle ? (
            <span>
              <span className="font-medium">{decodeHtmlEntities(activeAgendaItemTitle)}</span>
              <span className="text-slate-400"> — {currentItemNumber} of {topicRows.length} done</span>
            </span>
          ) : (
            <span>{currentItemNumber} of {topicRows.length} topics done</span>
          )}
        </div>

      </div>

      {/* Row 2: Current agenda item + Up Next */}
      {activeAgendaItemTitle ? (() => {
        const nextItem = activeIdx >= 0 && activeIdx < total - 1 ? topicRows[activeIdx + 1] : null
        return (
          <div className="hidden sm:flex items-center gap-6 border-t border-slate-100 mt-1 pt-3 pb-3 px-1">
            <div className="flex flex-col items-start gap-1 min-w-0">
              <span className="text-xs font-medium uppercase tracking-wider text-slate-400">
                Current agenda item
              </span>
              <div className="flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1">
                {isLive ? (
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                ) : null}
                <span className="text-sm font-bold text-emerald-800 truncate">{decodeHtmlEntities(activeAgendaItemTitle)}</span>
              </div>
            </div>
            {nextItem ? (
              <>
                <div className="self-stretch w-px bg-slate-200 shrink-0 my-0.5" />
                <div className="flex flex-col items-start gap-1 min-w-0">
                  <span className="text-xs font-medium uppercase tracking-wider text-slate-400">Up next</span>
                  <div className="flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-3 py-1">
                    <span className="text-sm font-medium text-slate-600 truncate">{decodeHtmlEntities(nextItem.title)}</span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )
      })() : null}
    </div>
  )
}
